import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import {
  FixedSizeList,
  ListChildComponentProps,
  VariableSizeList,
} from 'react-window';
import { Typography } from '@rossum/ui/material';
import { EnumOption } from '../../../../../types/schema';

const ListContext = createContext<{
  setSize: (index: number, size: number) => void;
  getSizeMap: () => Record<number, number>;
} | null>(null);

const useListContext = () => {
  const context = useContext(ListContext);

  if (context === null) {
    throw new Error(
      '`useListContext` must be used within a ListContext.Provider'
    );
  }

  return context;
};

type EnumOptionComponentProps = {
  option: EnumOption;
  props: React.HTMLAttributes<HTMLLIElement>;
  index: number;
};

const EnumOptionComponent = ({
  option,
  props,
  index,
}: EnumOptionComponentProps) => {
  const { setSize, getSizeMap } = useListContext();
  const root = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const sizeMap = getSizeMap();
    if (sizeMap[index] === undefined) {
      setSize(index, root.current?.getBoundingClientRect().height ?? 0);
    }
  }, [setSize, index, getSizeMap]);

  return (
    <Typography
      ref={root}
      variant="body2"
      component="li"
      {...props}
      sx={{
        overflowWrap: 'anywhere',
        listStyle: 'none',
        whiteSpace: 'pre',
      }}
      title={option.label}
    >
      {option.label || '\u00A0'}
    </Typography>
  );
};

export type RenderRowData = [
  React.HTMLAttributes<HTMLLIElement> & React.Attributes,
  EnumOption,
  number,
  string,
];

const renderRow = (
  renderRowProps: ListChildComponentProps<Array<RenderRowData>>
) => {
  const { data, index: renderRowIndex, style } = renderRowProps;
  const rowData = data[renderRowIndex];

  if (!rowData) {
    return null;
  }

  const [{ key, ...props }, option, index] = rowData;

  return (
    <EnumOptionComponent
      key={`${key}-${data.length}`}
      index={index}
      props={{ ...props, style: { ...props.style, ...style } }}
      option={option}
    />
  );
};

const OuterElementContext = createContext({});

const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const getStateSignature = (itemCount: number, inputValue: string) =>
  `${itemCount}-${inputValue}`;

export const VirtualizedListBox = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>((props, ref) => {
  const { children, ...other } = props;

  const itemData = children as Array<RenderRowData>;

  const inputValue = itemData[0]?.[3] ?? '';

  const itemCount = itemData.length;

  const listRef = useRef<VariableSizeList>(null);

  const sizeMap = useRef<Record<number, number>>({});

  // include inputValue as well since itemCount may stay the same,
  // but match-sorter can change the order of itemData,
  // in that case we want to reset sizeMap
  const prevStateSignature = useRef(getStateSignature(itemCount, inputValue));

  useLayoutEffect(() => {
    if (
      prevStateSignature.current !== getStateSignature(itemCount, inputValue)
    ) {
      listRef.current?.resetAfterIndex(0);
      listRef.current?.scrollTo(0);
      sizeMap.current = {};
      prevStateSignature.current = getStateSignature(itemCount, inputValue);
    }
  }, [inputValue, itemCount]);

  const setSize = useCallback(
    (index: number, size: number) => {
      sizeMap.current[index] = size;
      listRef.current?.resetAfterIndex(index);
    },
    [listRef]
  );

  const getSizeMap = useCallback(() => sizeMap.current, []);

  const listContextValue = useMemo(
    () => ({ setSize, getSizeMap }),
    [setSize, getSizeMap]
  );

  const height = useMemo(() => {
    // one line option is ~34px, 12 items is ~400px
    if (itemCount > 12) {
      // there is also `maxHeight: 40vh` set on the parent MUI component
      return 400;
    }

    return itemData.length * 30;
  }, [itemCount, itemData]);

  return (
    <ListContext.Provider value={listContextValue}>
      {/* TODO: use VirtualizedListboxFixedSize from ui components */}
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <FixedSizeList
            itemData={itemData}
            height={height}
            width="100%"
            itemCount={itemData.length}
            itemSize={30}
            outerElementType={OuterElementType}
            innerElementType="ul"
          >
            {renderRow}
          </FixedSizeList>
        </OuterElementContext.Provider>
      </div>
    </ListContext.Provider>
  );
});
